export class ExerciseHelper {
  constructor() {
    this.head = null;
    this.tail = null;
  }

  getHead() {
    return this.head;
  }

  setHead(head) {
    this.head = head;
  }

  createNode(block) {
    return {
      next: null,
      text: block?.basicInfo?.text || '',
      name: block?.basicInfo?.name || '',
      cbId: block.cbId,
      cbType: block.basicInfo.type,
      stepId: block.stepId || null,
      required: block.required,
      isAttemptable: true,
    };
  }

  addNode(block) {
    const node = this.createNode(block);
    if (!this.head) {
      node.isFirst = true;
      this.head = node;
      this.tail = node;
    } else {
      this.tail.next = node;
      this.tail = this.tail.next;
    }
    return node;
  }

  transformExercise(expandedExercise) {
    this.head = null;
    this.tail = null;

    if (!expandedExercise.contentBlocks) {
      return;
    }

    // Build a map for quick lookup
    const blockMap = new Map();
    expandedExercise.contentBlocks.forEach(cb => {
      blockMap.set(cb.cbId, { ...cb, children: [] });
    });

    // Build tree structure by attaching children to parents
    const mainSteps = [];
    expandedExercise.contentBlocks.forEach(cb => {
      if (!cb.stepId) {
        // This is a main step (root level)
        mainSteps.push(blockMap.get(cb.cbId));
      } else {
        // This is a substep, attach to parent (which could be a step or another substep)
        const parent = blockMap.get(cb.stepId);
        if (parent) {
          parent.children.push(blockMap.get(cb.cbId));
        }
      }
    });

    // Helper function to recursively collect all descendants in depth-first order
    const collectAllDescendants = block => {
      let descendants = [];

      if (block.children && block.children.length > 0) {
        block.children.forEach(child => {
          descendants.push(child);
          // Recursively collect child's descendants
          descendants = descendants.concat(collectAllDescendants(child));
        });
      }

      return descendants;
    };

    // Build linked list with ONLY main steps, but each main step contains all its descendants
    mainSteps.forEach(step => {
      // Collect all descendants (substeps and nested substeps) for this main step
      const allDescendants = collectAllDescendants(step);

      // Create node for main step with all its substeps embedded
      const mainStepNode = this.createNode(step);
      mainStepNode.subSteps = allDescendants; // Store all substeps in the main step
      mainStepNode.allItems = [step, ...allDescendants]; // Main step + all descendants for easy rendering

      // Add only the main step to the linked list (substeps are not separate nodes)
      if (!this.head) {
        mainStepNode.isFirst = true;
        this.head = mainStepNode;
        this.tail = mainStepNode;
      } else {
        this.tail.next = mainStepNode;
        this.tail = this.tail.next;
      }
    });
  }

  getContentBlock(cbId) {
    let current = this.head;
    while (current) {
      if (current.cbId === cbId) {
        return current;
      }
      current = current.next;
    }
    return null;
  }

  getNextBlock(cbId) {
    const current = this.getContentBlock(cbId);
    return current ? current.next : null;
  }

  getPreviousNode(cbId) {
    let current = this.head;
    while (current) {
      if (current.next && current.next.cbId === cbId) {
        return current;
      }
      current = current.next;
    }
    return null;
  }

  getPreviousBlock(cbId) {
    return this.getPreviousNode(cbId);
  }

  getAllSteps() {
    const steps = [];
    let current = this.head;
    while (current) {
      steps.push(current);
      current = current.next;
    }
    return steps;
  }

  getMainSteps() {
    // Now all nodes in the linked list are main steps (substeps are embedded in subSteps property)
    return this.getAllSteps();
  }

  // Find the root parent (main step) of any step/substep
  getRootParent(cbId) {
    const block = this.getContentBlock(cbId);
    if (!block || !block.stepId) {
      return block; // This is already a main step or doesn't exist
    }

    // Recursively find the root parent
    return this.getRootParent(block.stepId);
  }
}
